<script setup>
import { useAuth0 } from "@auth0/auth0-vue";
import WeatherWidget from "../components/widgets/WeatherWidget.vue";
import TimeWidget from "../components/widgets/TimeWidget.vue";
import NewsWidget from "../components/widgets/NewsWidget.vue";
import UserWidget from "../components/widgets/UserWidget.vue";
import CalendarWidget from "../components/widgets/CalendarWidget.vue";

const { logout, user, isAuthenticated } = useAuth0();
const handleLogout = () => {
  logout({ logoutParams: { returnTo: window.location.origin } });
};
</script>

<template>
  <div class="grid-x full-width">
    <div class="cell small-12 medium-offset-1 medium-10 large-offset-1 large-10 white-section content-section">
      <div class="grid-x">
        <div class="cell medium-3 show-for-medium-only"></div>
        <!-- Look into push/pull-->
        <div class="cell small-12 medium-6 large-3">
          <UserWidget :user="user" @logout="handleLogout"></UserWidget>
        </div>

        <div class="cell small-12 medium-12 large-9">
          <div class="main-content">
            <h2 class="left-align">Hi {{ user.meta_firstname }} | Profile</h2>

            <p>
              Welcome to your personal dashboard. Your central hub for staying connected to what matters around you.
              Designed to bring essential everyday information together in one place, this space automatically adapts to
              your location to keep you informed with minimal effort.
            </p>
            <p>
              Whether you're checking the local weather forecast before heading out, confirming accurate local time zone
              details, or skimming the latest regional headlines, everything is structured for quick, clear reading.
              Explore your widgets below to get a real-time snapshot of your day.
            </p>

            <h3 class="left-align">Local information</h3>

            <div class="grid-x">
              <div class="cell small-12 medium-offset-3 medium-6 large-offset-0 large-4 margin-bottom card-padding">
                <TimeWidget :user-location="user.meta_location.address_line2"></TimeWidget>
              </div>
              <div class="cell small-12 medium-offset-3 medium-6 large-4 large-offset-0 margin-bottom card-padding">
                <WeatherWidget :user-location="user.meta_location.address_line2"></WeatherWidget>
              </div>
              <div class="cell small-12 medium-offset-3 medium-6 large-offset-0  large-4 margin-bottom card-padding">
                <NewsWidget :user-location="user.meta_location.address_line2"></NewsWidget>
              </div>
            </div>


            <h3 class="left-align">Your diary</h3>
            <p>
              All of your diary information will be available soon.
            </p>

            <p>
              Take a look so far.
              <CalendarWidget></CalendarWidget>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use "@/assets/styles/abstract/variables" as *;

h2 {
  font-size: 30px;
}

h3 {
  font-size: 24px;
}

.main-content {
  padding: 1rem;
}

@include desktop {
  .card-padding {
    padding: 10px;
  }
}

$global-radius: 10px;
$white: rgba(255, 255, 255, 0.5);
$light-gray: #{$primary-color}+"66";


$card-user-container-margin-bottom: 1rem;
$card-user-container-radius: $global-radius;
$card-user-container-background: $white;
$card-user-container-color: $primary-color;
$card-user-container-image-size: calc(190px);

.card-user-container {
  margin: $card-user-container-margin-bottom;
  background-color: $card-user-container-background;
  color: $secondary-color;
  overflow: hidden;
  padding: 0;
  border-radius: $card-user-container-radius;

  .card-user-avatar {
    .user-image {
      display: block;
      margin: auto;
      border-radius: 50%;
      width: $card-user-container-image-size;
      height: $card-user-container-image-size;
      padding: calc(15px);

      &:hover {
        transform: scale(1.1);
      }
    }
  }

  .card-user-bio {
    padding: 1rem;
    text-align: center;

    .location {
      display: inline-block;
      margin: 0 calc(15px);
      color: $secondary-color;
      font-size: calc(14px);
    }

    .location-icon,
    .location-text {
      display: block;
      padding: 0 calc(5px);
    }
  }

  .card-user-social {
    background: $light-gray;
    padding: 1rem;
    text-align: center;

    ul {
      display: inline-block;

      li {
        margin: 0 10px;
        font-size: 1.2em;
        display: inline;
        cursor: pointer;

        svg:hover {
          color: $primary-color;
          transition: transform .2s;
          transform: scale(2);
        }
      }
    }
  }

  .card-user-button {
    text-align: center;
    padding: 1rem;
  }
}
</style>
